import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewChecked, AfterViewInit, Component, DestroyRef, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import { Menu } from './components/menu/menu';
import { AuthenticatedUser, AuthSessionService } from './services/auth-session.service';
import { SiteContextService } from './services/site-context.service';
import { SiteSummary } from './models/cms-models';
import { md5 } from './utils/md5';

const LAST_USED_EMAIL_STORAGE_KEY = 'southbound-cms.last-used-email';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    FormsModule,
    Menu,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements AfterViewInit, AfterViewChecked {
  @ViewChild('emailInput') private emailInput?: ElementRef<HTMLInputElement>;
  @ViewChild('codeInput') private codeInput?: ElementRef<HTMLInputElement>;

  auth = inject(AuthSessionService);
  private siteContext = inject(SiteContextService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private pendingAuthFocusTarget: 'email' | 'code' | null = 'email';

  email = '';
  code = '';
  otpRequested = false;
  infoMessage = '';
  errorMessage = '';
  requestingOtp = false;
  verifyingOtp = false;
  currentUser: AuthenticatedUser | null = null;
  gravatarUrl: string | null = null;
  sites: SiteSummary[] = [];
  activeSiteId: string | null = null;
  activeSiteName: string | null = null;
  userMenuOpen = false;
  siteDialogOpen = false;

  constructor() {
    this.email = this.restoreLastUsedEmail();

    this.auth.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.currentUser = user;
      this.gravatarUrl = user ? createGravatarUrl(user.email) : null;
    });

    this.auth.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        void this.siteContext.ensureLoaded();
        return;
      }

      this.userMenuOpen = false;
      this.siteContext.clearSites();
    });

    this.siteContext.sites$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sites) => {
      this.sites = sites;
      this.activeSiteName = sites.find((site) => site.id === this.activeSiteId)?.name ?? null;
    });

    this.siteContext.activeSiteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeSiteId) => {
      this.activeSiteId = activeSiteId;
      this.activeSiteName = this.sites.find((site) => site.id === activeSiteId)?.name ?? null;
    });
  }

  ngAfterViewInit(): void {
    this.applyPendingAuthFocus();
  }

  ngAfterViewChecked(): void {
    this.applyPendingAuthFocus();
  }

  async requestOtp(): Promise<void> {
    const email = this.email.trim();
    if (!email) {
      this.errorMessage = 'Enter your email address to receive a login code.';
      this.pendingAuthFocusTarget = 'email';
      return;
    }

    this.email = email;
    this.persistLastUsedEmail(email);
    this.requestingOtp = true;
    this.errorMessage = '';
    this.infoMessage = '';

    try {
      await this.auth.requestOtp(email);
      this.otpRequested = true;
      this.infoMessage = 'If your email is allowed, a 6-digit login code has been sent.';
      this.pendingAuthFocusTarget = 'code';
    } catch {
      this.errorMessage = 'Unable to request a login code right now.';
      this.pendingAuthFocusTarget = 'email';
    } finally {
      this.requestingOtp = false;
    }
  }

  async verifyOtp(): Promise<void> {
    const email = this.email.trim();
    if (!email || !this.code.trim()) {
      this.errorMessage = 'Enter both your email address and the 6-digit code.';
      this.pendingAuthFocusTarget = email ? 'code' : 'email';
      return;
    }

    this.email = email;
    this.persistLastUsedEmail(email);
    this.verifyingOtp = true;
    this.errorMessage = '';

    try {
      await this.auth.verifyOtp(email, this.code);
      this.code = '';
      this.infoMessage = '';
    } catch (error) {
      this.errorMessage = error instanceof HttpErrorResponse && error.status === 401
        ? 'The login code is invalid or has expired.'
        : 'Unable to verify the login code right now.';
      this.pendingAuthFocusTarget = 'code';
    } finally {
      this.verifyingOtp = false;
    }
  }

  onEmailEnter(event: Event): void {
    event.preventDefault();

    if (this.requestingOtp || this.verifyingOtp) {
      return;
    }

    void this.requestOtp();
  }

  onCodeEnter(event: Event): void {
    event.preventDefault();

    if (this.requestingOtp || this.verifyingOtp) {
      return;
    }

    void this.verifyOtp();
  }

  onCodeChange(value: string): void {
    this.code = value.replace(/\D/g, '').slice(0, 6);

    if (this.code.length !== 6 || !this.otpRequested || this.requestingOtp || this.verifyingOtp) {
      return;
    }

    void this.verifyOtp();
  }

  doLogout() {
    this.auth.logout();
    this.code = '';
    this.otpRequested = false;
    this.infoMessage = '';
    this.errorMessage = '';
    this.pendingAuthFocusTarget = 'email';
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }

  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  async changeActiveSite(siteId: string | null): Promise<void> {
    this.siteContext.setActiveSite(siteId || null);
    await this.syncCurrentViewWithActiveSite(siteId);
  }

  openSiteDialog(): void {
    this.userMenuOpen = false;
    this.siteDialogOpen = true;
  }

  closeSiteDialog(): void {
    this.siteDialogOpen = false;
  }

  async selectActiveSite(siteId: string): Promise<void> {
    await this.changeActiveSite(siteId);
    this.closeSiteDialog();
  }

  async openActiveSiteWorkspace(): Promise<void> {
    if (!this.activeSiteId) {
      return;
    }

    this.closeUserMenu();
    await this.router.navigate(['/sites', this.activeSiteId]);
  }

  async openSites(): Promise<void> {
    this.closeUserMenu();
    await this.router.navigate(['/sites']);
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.closeUserMenu();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closeUserMenu();
    this.closeSiteDialog();
  }

  private async syncCurrentViewWithActiveSite(siteId: string | null): Promise<void> {
    if (!siteId) {
      return;
    }

    const currentPath = this.router.url.split('?')[0] ?? '/';

    if (currentPath.startsWith('/sites/')) {
      await this.router.navigate(['/sites', siteId]);
      return;
    }

    if (currentPath.startsWith('/landing-pages/') && currentPath !== '/landing-pages/new') {
      await this.router.navigate(['/landing-pages']);
      return;
    }

    if (currentPath.startsWith('/links/') && currentPath !== '/links/new') {
      await this.router.navigate(['/links']);
      return;
    }

    if (currentPath.startsWith('/blogposts/') && currentPath !== '/blogposts/new') {
      await this.router.navigate(['/blogposts']);
    }
  }

  private applyPendingAuthFocus(): void {
    if (!this.pendingAuthFocusTarget) {
      return;
    }

    const target = this.pendingAuthFocusTarget === 'code'
      ? this.codeInput?.nativeElement
      : this.emailInput?.nativeElement;

    if (!target) {
      return;
    }

    if (document.activeElement !== target) {
      target.focus();
    }

    this.pendingAuthFocusTarget = null;
  }

  private restoreLastUsedEmail(): string {
    try {
      return localStorage.getItem(LAST_USED_EMAIL_STORAGE_KEY)?.trim() ?? '';
    } catch {
      return '';
    }
  }

  private persistLastUsedEmail(email: string): void {
    try {
      localStorage.setItem(LAST_USED_EMAIL_STORAGE_KEY, email);
    } catch {
      // Ignore storage failures and keep login usable.
    }
  }
}

function createGravatarUrl(email: string): string {
  const normalizedEmail = email.trim().toLowerCase();
  return `https://www.gravatar.com/avatar/${md5(normalizedEmail)}?d=identicon&s=96`;
}
